import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { RouteSeo } from "./Seo";
import { Menu, X, MapPin } from "lucide-react";
import { STATES_SEO } from "../seo/explore-data";

export const Wordmark = ({ dark }) => (
  <Link to="/" className="flex items-baseline gap-2 group" data-testid="nav-wordmark">
    <span className="font-sans font-extrabold text-xl tracking-tight">
      <span className="text-[#1D4ED8]">Reg</span>
      <span className="text-[#059669]">Nav</span>
    </span>
    <span className={`text-[10px] font-semibold tracking-[0.08em] ${dark ? "text-white/70" : "text-[#475569]"}`}>
      by <span className="text-[#1D4ED8]">Solar</span><span className="text-[#059669]">ian</span>
    </span>
  </Link>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = (
    <>
      <Link to="/wizard" className="text-sm font-medium hover:text-[#1D4ED8] transition-colors" data-testid="nav-wizard-link" onClick={() => setOpen(false)}>Map my approvals</Link>
      <Link to="/analyzer" className="text-sm font-medium hover:text-[#1D4ED8] transition-colors" data-testid="nav-analyzer-link" onClick={() => setOpen(false)}>Switch analyzer</Link>
      <Link to="/states" className="text-sm font-medium hover:text-[#1D4ED8] transition-colors" data-testid="nav-states-link" onClick={() => setOpen(false)}>State Approval List</Link>
      {user && <Link to="/projects" className="text-sm font-medium hover:text-[#1D4ED8] transition-colors" data-testid="nav-projects-link" onClick={() => setOpen(false)}>My projects</Link>}
      {user?.role === "admin" && <Link to="/admin" className="text-sm font-medium hover:text-[#1D4ED8] transition-colors" data-testid="nav-admin-link" onClick={() => setOpen(false)}>Knowledge base</Link>}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F8FF]">
      <RouteSeo />
      {user?.is_demo && (
        <div className="bg-[#0B1F3A] text-white text-center text-xs sm:text-sm py-2 px-4 font-medium" data-testid="demo-banner">
          You're exploring the demo account —{" "}
          <Link to="/signup" className="underline font-semibold text-[#34D399]">Create your free account</Link> to map your own project.
        </div>
      )}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[#0B1F3A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-6 text-[#0B1F3A]">
            {links}
            {user ? (
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#475569]" data-testid="nav-user-name">{user.name || user.email}</span>
                <button onClick={handleLogout} className="text-sm font-medium border border-[#0B1F3A]/25 rounded-md px-3 py-1.5 hover:bg-[#0B1F3A] hover:text-white transition-colors" data-testid="nav-logout-btn">Log out</button>
              </div>
            ) : (
              <Link to="/login" className="text-sm font-semibold bg-[#1D4ED8] text-white rounded-md px-4 py-1.5 hover:bg-[#1638A8] transition-colors" data-testid="nav-login-link">Log in</Link>
            )}
          </nav>
          <button className="md:hidden text-[#0B1F3A]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#0B1F3A]/10 px-4 py-4 flex flex-col gap-4 bg-white">
            {links}
            {user ? (
              <button onClick={handleLogout} className="text-sm font-medium text-left" data-testid="nav-logout-btn-mobile">Log out</button>
            ) : (
              <Link to="/login" className="text-sm font-medium" onClick={() => setOpen(false)}>Log in</Link>
            )}
          </div>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <footer className="band-navy text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-sans font-extrabold text-2xl tracking-tight">
              <span className="text-[#5B8CFF]">Reg</span><span className="text-[#34D399]">Nav</span>
              <span className="text-xs font-semibold text-white/60 ml-2">by <span className="text-[#5B8CFF]">Solar</span><span className="text-[#34D399]">ian</span></span>
            </p>
            <p className="text-sm text-white/70 mt-2 max-w-xs" data-testid="footer-fullform">
              <span className="font-semibold text-white">RegNav — Your Regulatory Navigator.</span> Every approval. Every department. One roadmap.
            </p>
          </div>
          <div className="text-xs leading-relaxed" data-testid="footer-explore">
            <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-white/50 mb-2">Explore</p>
            <Link to="/approvals" className="block text-white/70 hover:text-white py-0.5" data-testid="footer-approvals-link">Browse all approvals</Link>
            <Link to="/states" className="block text-white/70 hover:text-white py-0.5" data-testid="footer-states-link">State roadmaps</Link>
            <Link to="/glossary" className="block text-white/70 hover:text-white py-0.5" data-testid="footer-glossary-link">Regulatory glossary</Link>
            <Link to="/wizard" className="block text-white/70 hover:text-white py-0.5" data-testid="footer-wizard-link">Approval wizard</Link>
            <Link to="/analyzer" className="block text-white/70 hover:text-white py-0.5" data-testid="footer-analyzer-link">Switch analyzer</Link>
          </div>
          <div className="text-xs text-white/60 leading-relaxed">
            <a href="https://maps.app.goo.gl/HXZ26g3i53jvvWMx8" target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-white transition-colors" data-testid="footer-address-link">
              <MapPin size={13} className="mt-0.5 shrink-0 text-[#34D399]" />
              <span className="underline decoration-white/20 underline-offset-2">WeWork, Embassy Galaxy Business Park,<br />A-44 &amp; 45, Sushil Marg, Sector 62,<br />Noida, Uttar Pradesh 201309</span>
            </a>
            <a href="https://solarianastra.com" target="_blank" rel="noreferrer" className="inline-block mt-2 text-[#5B8CFF] hover:text-white transition-colors">solarianastra.com</a>
          </div>
          <div className="text-xs text-white/50 leading-relaxed">
            <p>Indicative guidance, not legal advice — verify with the concerned authority. Solarian provides expert execution support.</p>
            <p className="mt-3 text-white/35">Solarian Astra Pvt Ltd</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 -mt-2 border-t border-white/10 pt-4 text-[11px] text-white/45 leading-relaxed" data-testid="footer-state-links">
          <span className="font-semibold text-white/60">State approval roadmaps: </span>
          {STATES_SEO.map((s, i) => (
            <span key={s.slug}>{i > 0 && " · "}<Link to={`/states/${s.slug}`} className="hover:text-white">{s.name}</Link></span>
          ))}
        </div>
      </footer>
    </div>
  );
}

export async function oneClickDemo(setUser, navigate) {
  const { data } = await api.post("/auth/demo");
  setUser(data);
  track("demo_login");
  navigate("/projects");
}
